import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import SkeletonType1 from "@/app/components/ui/Skeletons/Type1";
import { useServiceDeskTicketListAPI } from "@/app/hooks/api_hooks/service_desk/useServiceDeskTicketListAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useUtils } from "@/app/hooks/useUtils";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { useEffect, useState } from "react";
import RemarksComponent from "@components/routes/auth_routes/VeticServiceDesk/RemarksComponent";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { PRIMARY_BUTTON } from "@/constants";

export interface IServiceTicketList {
  creation_date?: string;
  vetic_status?: string;
  search_keyword?: string;
  customer_user_id?: string;
}
export interface IServiceFiltersFormFields {
  issue_date: string;
  issue_status: string;
  search_ticket?: string;
}

const TicketTab = () => {
  const { getRouteKey } = useRouter();
  const { routeState } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"));
  const { customerId, customerDetails } = routeState.state as ICustomerProfileState;
  const { NO_PWL_RESULTS } = useImageConstants();
  const { veticDateFormat } = useUtils();

  const [open, setOpen] = useState(false);
  const [serviceDetails, setServiceDetails] = useState(null);

  const { execute: fetchTicketList, ticketList } = useServiceDeskTicketListAPI();
  const isTickeLoading = useLoader("isServiceDeskTicketList");

  useEffect(() => {
    if (customerId) {
      const constructedBody: IServiceTicketList = {
        customer_user_id: customerId,
      };
      fetchTicketList(constructedBody);
    }
  }, [customerId]);

  return (
    <>
      <div className="pt-2 pl-2 bg-white">
        <div className="flex items-center justify-between mb-3">
          <div className="sm:flex-auto">
            <h1 className="text-sm font-semibold leading-6 text-gray-900">Ticket Details</h1>
          </div>
          <button
            className={PRIMARY_BUTTON}
            onClick={() =>
              window.open(
                `/app/service-desk/raise-issue?mobile=${customerDetails?.mobile}`,
                "_blank"
              )
            }
          >
            Raise Ticket
          </button>
        </div>

        {isTickeLoading ? (
          <div>
            <TableSkeleton />
            {/* <SkeletonType1 className="mt-2" /> */}
          </div>
        ) : ticketList?.length > 0 ? (
          <div
            className="scroll-inner !h-auto max-h-[450px] 2xl:max-h-[550px] flow-root relative rounded-xl bg-[#F9FAFB]"
          >
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full align-middle">
                <table className="w-full min-w-[680px] sm:min-w-[760px] md:min-w-[980px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                  <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal">
                    <tr>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                        Customer
                      </th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                        Ticket ID
                      </th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                        Remarks
                      </th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                        Creation Date
                      </th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                        Issue In
                      </th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                        Source
                      </th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                        Status
                      </th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                        Attachments
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {ticketList?.map((item, index) => {
                      const isFirst = index === 0;
                      const isLast = index === ticketList.length - 1;
                      const isSingle = ticketList.length === 1;

                      return (
                        <tr
                          key={item?.created_at}
                          className={`
                            bg-white
                            ${isSingle ? "rounded-xl" : ""}
                            ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                            ${isLast && !isSingle ? "rounded-b-xl" : ""}
                          `}
                        >
                          {/* Customer */}
                          <td
                            className={`
                              whitespace-nowrap px-2 py-4 text-xs font-medium text-black
                              ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                              ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                              ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                            `}
                          >
                            {item?.user_name || "NA"}
                          </td>

                          {/* Ticket ID */}
                          <td className="whitespace-nowrap px-2 py-3 text-xs text-[#0068FF] font-medium">
                            {item?.vetic_ticket_id}
                          </td>

                          {/* Remarks */}
                          <td className="whitespace-nowrap px-2 py-3 text-xs text-black">
                            {item?.latest_private_comment === "" && item?.latest_public_comment === "" ? (
                              <span>—</span>
                            ) : (
                              <p
                                className="text-xs text-blue-500 cursor-pointer"
                                onClick={() => {
                                  setServiceDetails(item);
                                  setOpen(true);
                                }}
                              >
                                View Remarks
                              </p>
                            )}
                          </td>

                          {/* Creation Date */}
                          <td className="whitespace-nowrap px-2 py-3 text-xs text-black">
                            {veticDateFormat(item?.created_at) || "NA"}
                          </td>

                          {/* Issue In */}
                          <td className="whitespace-nowrap px-2 py-3 text-xs text-black">
                            {item?.l1_heading || "NA"}
                          </td>

                          {/* Source */}
                          <td className="whitespace-nowrap px-2 py-3 text-xs text-black">
                            {item?.source_of_ticket || "NA"}
                          </td>

                          {/* Status */}
                          <td className="whitespace-nowrap px-2 py-3 text-xs">
                            <p
                              className={`font-medium text-xs ${item?.vetic_status?.toLowerCase() === "pending"
                                ? "text-[#B78811]"
                                : "text-[#058E34]"
                                }`}
                            >
                              {item?.vetic_status || "NA"}
                            </p>
                          </td>

                          {/* Attachments */}
                          <td
                            className={`
                              whitespace-nowrap px-2 py-3 text-xs text-black
                              ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""}
                              ${isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                              ${isLast && !isSingle ? "rounded-br-2xl" : ""}
                            `}
                          >
                            {item?.attachment_url?.length > 0 ? (
                              <div className="flex flex-col gap-1">
                                {item?.attachment_url?.map((val, idx) => (
                                  <a
                                    target="_blank"
                                    className="text-blue-400 underline"
                                    key={`emr_link:${idx + 1}`}
                                    href={val}
                                  >
                                    {`file ${idx + 1}`}
                                  </a>
                                ))}
                              </div>
                            ) : (
                              <span>NA</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <NoDataFoundComponent
            imageURL={NO_PWL_RESULTS}
            message="No Ticket Found"
            description="No Ticket found for the given filter, try again"
          />
        )}
      </div>

      {open && (
        <RemarksComponent
          open={open}
          setOpen={setOpen}
          serviceDetails={serviceDetails}
          setServiceDetails={setServiceDetails}
        />
      )}
    </>
  );
};

export default TicketTab;
